import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Package2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useDropdowns } from "@/hooks/useMeta";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export function SampleRequestDialog({ open, onOpenChange, context = {}, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const { data: dd } = useDropdowns();
  const types = dd?.sample_type || ["Free", "Paid"];

  const { data: products = [] } = useQuery({
    queryKey: ["products-active"],
    queryFn: async () => {
      try { return (await api.get("/products")).data || []; }
      catch { return []; }
    },
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (open) setForm({ sample_type: types[0] || "Free", quantity: 1, shipping_address: "", product_name: "", notes: "" });
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/samples", {
        ...form,
        lead_id: context.lead_id || null,
        opportunity_id: context.opportunity_id || null,
        company: context.company || "",
        contact_name: context.contact_name || "",
        quantity: Number(form.quantity) || 1,
      });
      toast.success("Sample requested");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="sample-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Package2 className="h-4 w-4 text-brand" /> Request a Sample</DialogTitle>
          <DialogDescription>{context.company ? `For ${context.company}` : "Send a product sample to the client."}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-700">Product</Label>
            <Select value={form.product_name || ""} onValueChange={(v) => set("product_name", v === "__none" ? "" : v)}>
              <SelectTrigger className="h-9" data-testid="sample-product"><SelectValue placeholder="Select product (optional)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">— Sample kit —</SelectItem>
                {products.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-700">Type</Label>
              <Select value={form.sample_type} onValueChange={(v) => set("sample_type", v)}>
                <SelectTrigger className="h-9" data-testid="sample-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-700">Quantity</Label>
              <Input type="number" min="1" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} className="h-9" data-testid="sample-qty" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-700">Shipping Address</Label>
            <Textarea value={form.shipping_address} onChange={(e) => set("shipping_address", e.target.value)} rows={2} placeholder="Where to ship the sample" data-testid="sample-address" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-700">Notes</Label>
            <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} placeholder="Any special instructions" data-testid="sample-notes" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="sample-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-brand hover:bg-brand/90 text-white gap-2" data-testid="sample-submit">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Send Request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
